// The list of chats (#141), without React: what a row says, how rows sort and filter, and how the open server's
// live state (its socket) replaces what the poll last said about it. Plain functions, so scripts/chats-cases.mjs can
// test them without a browser.
import { decodeChannelRefs, markdownToPlain, SPOILER_MASK, type Channel, type ChatSummary, type Message } from "@bloom/shared";

/** A row in the list: the summary, plus whether its newest message is still on its way (a poll will bring it). */
export interface ChatRow extends ChatSummary {
  loading?: boolean;
}

const DAY_MS = 86_400_000;
const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const weekdayFmt = new Intl.DateTimeFormat(undefined, { weekday: "long" });
const shortDateFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const longDateFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" });

/** Calendar days from `ts` to `now` in the local time zone (0: today, 1: yesterday). */
export function daysAgo(ts: number, now: number): number {
  const a = new Date(ts);
  const b = new Date(now);
  return Math.round((Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) - Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) / DAY_MS);
}

/** The time on the right of a row: "2:41 PM" today, "Yesterday", the weekday this week, else a short date. */
export function chatTime(ts: number, now: number): string {
  const d = daysAgo(ts, now);
  if (d <= 0) return timeFmt.format(ts);
  if (d === 1) return "Yesterday";
  if (d < 7) return weekdayFmt.format(ts);
  return (new Date(ts).getFullYear() === new Date(now).getFullYear() ? shortDateFmt : longDateFmt).format(ts);
}

const isPicture = (a: NonNullable<Message["attachments"]>[number]) => a.kind === "gif" || (a.kind === "file" && (/^image\//.test(a.type) || /^video\//.test(a.type)));

/**
 * A message as one line of plain text without its author: the same words as its push (worker/src/notify.ts
 * messageText), which the server uses for the polled rows. Formatting marks dropped, spoilers masked, tokens turned
 * into names, "sent an image" for a picture with no words; at most 180 characters.
 */
export function chatPreview(m: Message, userNames: Map<string, string>, channelNames: Map<string, string>): string {
  const plain = (body: string) => decodeChannelRefs(markdownToPlain(body, { spoiler: SPOILER_MASK }), channelNames).replace(/<@([A-Za-z0-9]+)>/g, (_, id: string) => `@${userNames.get(id) ?? "someone"}`);
  let text = plain(m.body);
  if (!text.trim() && m.forward) {
    const copied = plain(m.forward.body).trim();
    text = copied ? `forwarded: ${copied}` : "forwarded a message";
  }
  if (!text.trim() && m.attachments?.length) {
    const n = m.attachments.length;
    const priv = m.attachments.every((a) => a.private);
    text = m.attachments.some((a) => a.kind === "sticker") ? "sent a sticker" : m.attachments.every(isPicture) ? (n > 1 ? `sent ${n} ${priv ? "private " : ""}images` : priv ? "sent a private image" : "sent an image") : "sent a file";
  }
  text = text.replace(/\s+/g, " ").trim();
  if (text.length > 180) text = `${text.slice(0, 179)}…`;
  return text;
}

/** A DM's name: the other people's names ("Maya, Sam"). Text channels: the name. */
export function chatTitle(c: Pick<ChatSummary, "kind" | "name" | "members">, me: string | null, names: Map<string, string>): string {
  if (c.kind !== "dm") return c.name;
  const others = (c.members ?? []).filter((id) => id !== me);
  return others.map((id) => names.get(id) ?? "Someone").join(", ") || "Direct message";
}

/**
 * Circle icon letters for a text channel: first letter of each word (split on hyphens, underscores,
 * or whitespace), capped at three letters (e.g. "general" -> "G", "expensive-stuff" -> "ES",
 * "thats-so-jurassic" -> "TSJ").
 */
export function channelInitials(name: string): string {
  const words = name
    .trim()
    .split(/[-_\s]+/)
    .filter(Boolean);
  const letters = words
    .map((w) => {
      const m = w.match(/[\p{L}\p{N}]/u);
      return m ? m[0].toUpperCase() : "";
    })
    .filter(Boolean)
    .slice(0, 3)
    .join("");
  return letters || name.slice(0, 1).toUpperCase() || "#";
}

/**
 * Initials for quick switcher matching: extracts the first letter/digit of each word
 * (split on hyphens, underscores, spaces, or commas).
 * Returns { badge: 3-letter badge (matching channel initials), all: all word initials }.
 */
export function switcherInitials(label: string): { badge: string; all: string } {
  const words = label
    .trim()
    .split(/[-_\s,]+/)
    .filter(Boolean);
  const letters = words
    .map((w) => {
      const m = w.match(/[\p{L}\p{N}]/u);
      return m ? m[0].toLowerCase() : "";
    })
    .filter(Boolean);
  return {
    badge: letters.slice(0, 3).join(""),
    all: letters.join(""),
  };
}

/**
 * Scores a switcher item against a query:
 * 0: starts with query;
 * 1: a word starts with it;
 * 2: initials match it (e.g. "TS" for "that's so jurassic", "EH" for "expensive-hobbies");
 * 3: contains it;
 * null: no match.
 */
export function switcherScore(label: string, q: string): number | null {
  const l = label.toLowerCase();
  if (l.startsWith(q)) return 0;
  if (new RegExp(`[\\s,_-]${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(l)) return 1;
  const inits = switcherInitials(label);
  const qCompact = q.replace(/[\s,_-]+/g, "");
  if (qCompact && (inits.badge.startsWith(qCompact) || inits.all.startsWith(qCompact))) return 2;
  return l.includes(q) ? 3 : null;
}

/** A row's key in the list. */
export const chatKey = (c: Pick<ChatRow, "serverId" | "channelId">) => `${c.serverId}/${c.channelId}`;

/**
 * Which listed rows show their server's name beside the title. A text channel always does. A DM does only when
 * another listed DM has the same title (the same person in two servers), so the two can be told apart. Pass the rows
 * the list shows (closed DMs left out); titles are compared ignoring case. Returns the keys (`chatKey`) of the DMs.
 */
export function dmsNeedingServer(listed: ChatRow[], me: string | null, names: Map<string, string>): Set<string> {
  const byTitle = new Map<string, string[]>();
  for (const c of listed) {
    if (c.kind !== "dm") continue;
    const title = chatTitle(c, me, names).trim().toLowerCase();
    byTitle.set(title, [...(byTitle.get(title) ?? []), chatKey(c)]);
  }
  return new Set([...byTitle.values()].filter((keys) => keys.length > 1).flat());
}

/** The two lines under a row's name: "Author: text" (channels and group DMs), the text alone (1:1 DMs), "You: text" for mine. */
export function chatPreviewLine(c: ChatRow, me: string | null, names: Map<string, string>): string {
  if (c.lastTs === null) return c.loading ? "" : "No messages yet";
  const text = c.preview || "Message";
  const mine = !!me && c.lastAuthorId === me;
  const oneToOne = c.kind === "dm" && (c.members ?? []).length <= 2;
  if (mine) return `You: ${text}`;
  if (oneToOne) return text;
  return `${(c.lastAuthorId && names.get(c.lastAuthorId)) || "Someone"}: ${text}`;
}

/** Newest first; chats with no messages last, by name. */
export function sortChats<T extends Pick<ChatRow, "lastTs" | "serverId" | "channelId">>(chats: T[]): T[] {
  return [...chats].sort((a, b) => (b.lastTs ?? -1) - (a.lastTs ?? -1) || `${a.serverId}/${a.channelId}`.localeCompare(`${b.serverId}/${b.channelId}`));
}

/** The rows the search box and the server chip leave: `query` matches the chat's name, its server's name, its DM names and its preview. */
export function filterChats(chats: ChatRow[], opts: { query: string; serverId: string | null; me: string | null; names: Map<string, string>; serverNames: Map<string, string> }): ChatRow[] {
  const q = opts.query.trim().toLowerCase();
  return chats.filter((c) => {
    if (c.closed) return false;
    if (opts.serverId && c.serverId !== opts.serverId) return false;
    if (!q) return true;
    return [chatTitle(c, opts.me, opts.names), opts.serverNames.get(c.serverId) ?? "", c.preview].some((s) => s.toLowerCase().includes(q));
  });
}

/** A message as the socket holds it (the web app's ChatMessage): `state` is set on one I sent that the room hasn't confirmed. */
export type HeldMessage = Message & { state?: "pending" | "failed" };

/** What the open server's socket knows, as the list needs it (the page passes the room's snapshot). */
export interface LiveServer {
  serverId: string;
  me: string | null;
  channels: Channel[];
  /** Messages by conversation id, oldest first (threads have their own streams, which this never reads). */
  byChannel: Record<string, { messages: HeldMessage[] } | undefined>;
  read: Record<string, number>;
  mentionCounts: Record<string, number>;
  dmClosed: Record<string, number>;
  muted: Set<string>;
  /** The open conversation: shown as read, and a closed DM that is open stays in the list. */
  activeId?: string;
  names: Map<string, string>;
}

/**
 * The open server's rows, built from its live socket so they never wait for a poll: unread, mentions, mutes and
 * closed DMs always; the newest message when the socket holds the end of the conversation (then an edit, a delete or
 * a new message shows at once), else what the poll last said. `stale`: a conversation moved on past the poll and the
 * socket doesn't hold its newest message, so the list should poll again soon.
 */
export function liveRows(live: LiveServer, polled: ChatRow[]): { rows: ChatRow[]; stale: boolean } {
  const prior = new Map(polled.filter((p) => p.serverId === live.serverId).map((p) => [p.channelId, p]));
  const channelNames = new Map(live.channels.filter((c) => c.kind === "text").map((c) => [c.id, c.name]));
  let stale = false;
  const rows = live.channels.map((c): ChatRow => {
    const before = prior.get(c.id);
    const held = live.byChannel[c.id]?.messages ?? [];
    // From the end: the newest confirmed message (does the socket hold the conversation's end?), whether one of mine
    // is still pending, and the newest message that is news (not deleted, not a quiet line).
    let tailSeq = -1;
    let pending = false;
    let news: HeldMessage | undefined;
    for (let i = held.length - 1; i >= 0 && (tailSeq < 0 || !news); i--) {
      const m = held[i]!;
      if (m.state) pending = true;
      else if (tailSeq < 0) tailSeq = m.seq;
      if (!news && !m.deleted && !m.system) news = m;
    }
    const last = tailSeq >= c.lastSeq || pending ? news : undefined;
    const muted = live.muted.has(c.id);
    const base = {
      serverId: live.serverId,
      channelId: c.id,
      kind: c.kind,
      name: c.name,
      ...(c.kind === "dm" ? { members: c.members } : {}),
      lastSeq: c.lastSeq,
      unread: !muted && c.lastSeq > (live.read[c.id] ?? 0),
      mentions: live.mentionCounts[c.id] ?? 0,
      muted,
      closed: c.kind === "dm" && c.id !== live.activeId && live.dmClosed[c.id] !== undefined && live.dmClosed[c.id]! >= c.lastSeq,
    };
    if (last) {
      return { ...base, lastTs: last.ts, lastAuthorId: last.authorId, preview: chatPreview(last, live.names, channelNames) };
    }
    if (before && before.lastSeq >= c.lastSeq) return { ...base, lastTs: before.lastTs, lastAuthorId: before.lastAuthorId, preview: before.preview };
    // The socket doesn't hold the newest message and the poll is older than it. Nothing was ever said, or we wait.
    if (c.lastSeq === 0) return { ...base, lastTs: null, lastAuthorId: null, preview: "" };
    stale = true;
    return { ...base, lastTs: before?.lastTs ?? null, lastAuthorId: before?.lastAuthorId ?? null, preview: before?.preview ?? "", loading: !before };
  });
  return { rows, stale };
}

/** Every server's rows: the poll's for the others, the live ones for the open server once its socket has told us. */
export function mergeChats(polled: ChatRow[], open: { serverId: string; rows: ChatRow[] } | null): ChatRow[] {
  const others = open ? polled.filter((c) => c.serverId !== open.serverId) : polled;
  return sortChats([...others, ...(open?.rows ?? [])]);
}

// ---- The server chip, remembered on this device ----
const FILTER_KEY = "bloom:chats-server";

/** The server the chips were last set to ("" or missing: All). Storage can be missing or throw; then it's All. */
export function loadChatServer(): string | null {
  try {
    return localStorage.getItem(FILTER_KEY) || null;
  } catch {
    return null;
  }
}

export function saveChatServer(serverId: string | null) {
  try {
    if (serverId) localStorage.setItem(FILTER_KEY, serverId);
    else localStorage.removeItem(FILTER_KEY);
  } catch {
    // Not remembered; it still applies until the page reloads.
  }
}
